import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { adminApi } from '../services/api';

// Per-field metadata. Adding a new AppConfig key? Add it here too — the form
// falls back to a plain text input with the bare API key as the label when
// metadata is missing, but operators benefit from the description + the
// right input control.
//
// Fields:
//   label        — friendly title shown above the input
//   description  — one or two sentences: what does it do, where does it show up
//   group        — pinned group; if absent, the `groupKey()` heuristic decides
//   type         — text (default) | longText | integer | url | email | color | date
//   example      — placeholder text + shown in the format hint
//   format       — short note on the expected shape (e.g. "Hex colour code")
//   min/max      — for integer inputs
//   internal     — true if this key is set by the system (e.g. cron watermark);
//                  hidden from the UI entirely
const FIELD_META = {
  // ─── Branding ──────────────────────────────────────────────────────────
  brand_primary_colour: {
    label: 'Brand primary colour',
    description: 'Used for CTAs (green "Confirm With Your Lenders" button in the notification email + "Open email to X" button on the landing page), progress bar, and the accent dot in the Fair wordmark.',
    group: 'Branding',
    type: 'color',
    format: 'Hex colour code',
    example: '#15A24A',
  },
  brand_logo_url: {
    label: 'Brand logo URL',
    description: 'Header logo shown at the top of the client landing page AND at the top of the notification email. Leave empty to use the bundled Fair wordmark+tagline asset (recommended). Override only when Fair supplies an updated asset hosted elsewhere — must be publicly reachable (no auth, no signed URL). PNG is most reliable across email clients; SVG works in browsers but Gmail will not render it in emails.',
    group: 'Branding',
    type: 'url',
    format: 'HTTPS URL to a PNG (preferred) or SVG',
    example: 'https://fair.inc/logo.png',
  },

  // ─── Email routing ─────────────────────────────────────────────────────
  bcc_routing_address: {
    label: 'BCC audit address',
    description: 'Every per-lender email the client sends is BCC\'d to this address. Mail Magnet ingests it into the CRM Email module, where the BCC reconciliation cron matches it to a lender row.',
    group: 'Email routing',
    type: 'email',
    example: 'fair-bcc-audit@fair.inc',
  },
  sender_domain: {
    label: 'Sender domain',
    description: 'Domain part of the notification email "From" address. Must be a domain you\'ve verified in ZeptoMail with DKIM and SPF.',
    group: 'Email routing',
    type: 'text',
    format: 'Bare domain (no protocol)',
    example: 'mail.fair.inc',
  },
  sender_email: {
    label: 'Sender email (full address, optional)',
    description: 'Explicit full "From" address. If set, overrides the noreply@<sender_domain> fallback. Use this when ZeptoMail is verified for an address whose local-part is something other than "noreply".',
    group: 'Email routing',
    type: 'email',
    example: 'notifications@fair.inc',
  },
  sender_name: {
    label: 'Sender display name',
    description: 'Friendly name shown alongside the From address in the client\'s inbox.',
    group: 'Email routing',
    type: 'text',
    example: 'Fair Inc',
  },
  support_email: {
    label: 'Support email',
    description: 'Shown to clients on error screens and on the notification email footer. Also used as the Reply-To address on every notification email.',
    group: 'Email routing',
    type: 'email',
    example: 'claims@fair.inc',
  },
  support_phone: {
    label: 'Support phone number',
    description: 'Phone number shown in the notification email body ("contact us on …"). Use the human-readable form, not E.164.',
    group: 'Email routing',
    type: 'text',
    example: '0161 528 1777',
  },

  // ─── Notification email templates ─────────────────────────────────────
  notification_email_subject_template: {
    label: 'Notification email subject',
    description: 'Subject line of the email sent to claimants. Merge variables: {first_name}, {lender_count}.',
    group: 'Notification email templates',
    type: 'text',
    example: 'Action needed: confirm your lender emails',
  },
  notification_email_body_template: {
    label: 'Notification email body (HTML)',
    description: 'HTML body of the notification email. Leave empty to use the built-in branded default (header band + Fair wordmark + green CTA + legal footer). Merge variables: {first_name}, {lender_count}, {magic_link}, {client_reference}, {support_phone}, {support_email}, {brand_primary_colour}, {legal_disclosure}, {current_year}.',
    group: 'Notification email templates',
    type: 'longText',
    format: 'HTML',
  },
  notification_email_text_template: {
    label: 'Notification email body (plain text)',
    description: 'Plain-text fallback for email clients that don\'t render HTML. Same merge variables as the HTML body. Leave empty to use the built-in default.',
    group: 'Notification email templates',
    type: 'longText',
  },

  // ─── Per-lender email templates (used in the client\'s mailto: draft) ─
  per_lender_email_subject_template: {
    label: 'Per-lender email subject',
    description: 'Subject line for the email the CLIENT sends to each lender (pre-filled in the mailto: draft). Should include the lender name so the BCC reconciliation cron can match it. Merge variables: {lender_name}, {agreement_number}.',
    group: 'Per-lender email templates',
    type: 'text',
    example: 'Request to remain represented by Fair — {lender_name}',
  },
  per_lender_email_body_template: {
    label: 'Per-lender email body',
    description: 'Body of the email the CLIENT sends to each lender (pre-filled in the mailto: draft). Plain text only — mailto: links don\'t support HTML reliably. The default uses the May-2026 brief copy authorising "Reclaims 4 U Ltd t/a Fair". Merge variables: {first_name}, {client_name}, {lender_name}, {agreement_number}.',
    group: 'Per-lender email templates',
    type: 'longText',
  },

  // ─── Landing + completion copy ────────────────────────────────────────
  landing_greeting_copy: {
    label: 'Landing page greeting',
    description: 'Sub-heading shown under the client\'s name on the declare landing page. Leave empty to use the built-in default ("To help us continue progressing your claim, your lender has asked you to confirm your authority…").',
    group: 'Landing + completion copy',
    type: 'longText',
  },
  completion_copy: {
    label: 'Completion screen extra text',
    description: 'Additional message appended below the standard "Confirmation Complete" thank-you. Leave empty to show just the default copy.',
    group: 'Landing + completion copy',
    type: 'longText',
  },
  legal_disclosure: {
    label: 'Legal disclosure (FCA + ICO)',
    description: 'Rendered in the dark footer band on every client-facing page AND at the bottom of the notification email. Update here when the legal team revises wording — no code redeploy required.',
    group: 'Landing + completion copy',
    type: 'longText',
  },

  // ─── Operations / guardrails ───────────────────────────────────────────
  dispatch_rate_per_min: {
    label: 'Dispatch rate per minute',
    description: 'Maximum NotificationDispatch rows the dispatchQueue cron will send per minute. Caps the burst rate to stay under ZeptoMail and Zoho CRM rate limits.',
    group: 'Operations',
    type: 'integer',
    min: 1,
    max: 500,
    example: '50',
  },
  cron_interval_minutes: {
    label: 'Cron interval (minutes)',
    description: 'Reference value documenting how often dispatchQueue + bccReconciliation are configured to run. Change the actual schedule in the Catalyst console, not here.',
    group: 'Operations',
    type: 'integer',
    min: 1,
    example: '5',
  },
  notification_min_interval_minutes: {
    label: 'Min interval between notifications (minutes)',
    description: 'Anti-spam guard. After a successful dispatch, the CRM workflow trigger cannot fire another notification for the same contact within this window. Ops Resend and bounce retry bypass this check.',
    group: 'Operations',
    type: 'integer',
    min: 0,
    example: '60',
  },
  bounce_retry_max_attempts: {
    label: 'Bounce retry max attempts',
    description: 'How many times the bounceRetry cron will re-queue a soft-bounced row before escalating it to a hard bounce and giving up. PRD-002 specifies 1.',
    group: 'Operations',
    type: 'integer',
    min: 0,
    max: 5,
    example: '1',
  },
  dispatch_stuck_timeout_minutes: {
    label: 'Dispatch stuck timeout (minutes)',
    description: 'Rows stuck in the "sending" state for longer than this (worker crashed mid-send) get reset to "failed" by the queue sweep at the start of each dispatchQueue tick. A Cliq alert fires.',
    group: 'Operations',
    type: 'integer',
    min: 1,
    example: '10',
  },
  quarantine_alert_threshold: {
    label: 'Quarantine alert threshold',
    description: 'Cliq alert fires when more than this many BCCQuarantine rows are open in the last 24h.',
    group: 'Operations',
    type: 'integer',
    min: 0,
    example: '5',
  },

  // ─── Lifecycle ─────────────────────────────────────────────────────────
  token_ttl_days: {
    label: 'Magic-link token TTL (days)',
    description: 'How long a Declaration_Token stays valid after issuance. After this, the client landing page shows "This link has expired" and ops must Regenerate.',
    group: 'Lifecycle',
    type: 'integer',
    min: 1,
    example: '30',
  },
  go_live_date: {
    label: 'Go-live date',
    description: 'Window start for the dashboard metrics. Leave empty to count since the table\'s first row.',
    group: 'Lifecycle',
    type: 'date',
    format: 'YYYY-MM-DD',
  },
  event_log_retention_days: {
    label: 'EventLog retention (days)',
    description: 'Reference value for a future cleanup cron. Rows older than this would be purged. Not enforced in MVP.',
    group: 'Lifecycle',
    type: 'integer',
    min: 30,
    example: '395',
  },
  max_reminders: {
    label: 'Max reminder emails',
    description: 'Reserved for the future reminder cron (PRD-002 §6 Phase 1.5). Not used in MVP.',
    group: 'Lifecycle',
    type: 'integer',
    min: 0,
    example: '3',
  },
  reminder_interval_days: {
    label: 'Reminder interval (days)',
    description: 'Reserved for the future reminder cron. Not used in MVP.',
    group: 'Lifecycle',
    type: 'integer',
    min: 1,
    example: '7',
  },

  // ─── Alerts ────────────────────────────────────────────────────────────
  cliq_alert_channel: {
    label: 'Cliq alert channel',
    description: 'Cliq channel name (include the leading "#") that receives ops alerts: hard bounces, stuck dispatches, quarantine over threshold, CRM auth failures.',
    group: 'Alerts',
    type: 'text',
    example: '#fair-lender-declaration-ops',
  },

  // ─── Internal — hidden from UI ─────────────────────────────────────────
  bcc_reconciliation_watermark: {
    internal: true, // set by the BCC reconciliation cron
  },
};

function groupKey(meta, key) {
  if (meta.group) return meta.group;
  if (key.startsWith('brand_')) return 'Branding';
  if (key.startsWith('notification_email_')) return 'Notification email templates';
  if (key.startsWith('per_lender_email_')) return 'Per-lender email templates';
  if (key.includes('landing') || key.includes('completion')) return 'Landing + completion copy';
  if (key.includes('cliq')) return 'Alerts';
  if (key.includes('dispatch') || key.includes('cron') || key.includes('quarantine')) return 'Operations';
  if (key.includes('bcc') || key.includes('sender') || key.includes('support')) return 'Email routing';
  if (key.includes('token') || key.includes('go_live') || key.includes('retention')) return 'Lifecycle';
  return 'Other';
}

const GROUP_ORDER = [
  'Branding',
  'Email routing',
  'Notification email templates',
  'Per-lender email templates',
  'Landing + completion copy',
  'Operations',
  'Alerts',
  'Lifecycle',
  'Other',
];

function inputTypeFor(metaType) {
  switch (metaType) {
    case 'integer': return 'number';
    case 'url': return 'url';
    case 'email': return 'email';
    case 'date': return 'date';
    case 'color': return 'color';
    default: return 'text';
  }
}

function prettifyKey(key) {
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default function AdminSettings() {
  const { isAdmin } = useOutletContext();
  const [entries, setEntries] = useState([]);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const load = () => {
    setLoading(true);
    adminApi.getConfig()
      .then((r) => { setEntries(r.entries || []); setEdits({}); })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const e of entries) {
      const meta = FIELD_META[e.settingKey] || {};
      if (meta.internal) continue;
      const group = groupKey(meta, e.settingKey);
      (g[group] = g[group] || []).push(e);
    }
    for (const arr of Object.values(g)) {
      arr.sort((a, b) => {
        const la = (FIELD_META[a.settingKey]?.label || a.settingKey).toLowerCase();
        const lb = (FIELD_META[b.settingKey]?.label || b.settingKey).toLowerCase();
        return la.localeCompare(lb);
      });
    }
    return g;
  }, [entries]);

  const dirtyCount = Object.keys(edits).length;

  const save = async () => {
    if (dirtyCount === 0) return;
    setSaving(true);
    try {
      const payload = Object.entries(edits).map(([settingKey, settingValue]) => ({ settingKey, settingValue }));
      await adminApi.saveConfig(payload);
      setToast(`Saved ${payload.length} setting${payload.length === 1 ? '' : 's'}`);
      setTimeout(() => setToast(null), 2000);
      load();
    } catch (err) {
      alert(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const valueFor = (e) => edits[e.settingKey] ?? e.settingValue ?? e.defaultValue ?? '';

  if (loading) return <div className="card"><span className="spinner" /></div>;
  if (error) return <div className="card"><span className="badge danger">Error</span> {error}</div>;

  if (!isAdmin) {
    return <div className="card"><span className="badge warn">Read-only</span> You can view settings but not edit them.</div>;
  }

  const orderedGroups = GROUP_ORDER.filter((g) => grouped[g]).concat(
    Object.keys(grouped).filter((g) => !GROUP_ORDER.includes(g))
  );

  return (
    <div className="stack-lg">
      <div className="row space">
        <h1 className="h1">Settings</h1>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn secondary" onClick={load} disabled={saving}>Reload</button>
          <button className="btn" onClick={save} disabled={saving || dirtyCount === 0}>
            {saving ? 'Saving…' : dirtyCount === 0 ? 'No changes' : `Save ${dirtyCount} change${dirtyCount === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>

      <div className="card small muted">
        Changes take effect within ~5 seconds across all function instances. Secrets (API keys, JWT secret) live in Catalyst Console env vars, not here.
      </div>

      {orderedGroups.map((group) => {
        const rows = grouped[group];
        return (
          <div key={group} className="card">
            <h2 className="h2">{group}</h2>
            <div className="stack-lg">
              {rows.map((e) => {
                const meta = FIELD_META[e.settingKey] || {};
                const label = meta.label || prettifyKey(e.settingKey);
                const isLong = meta.type === 'longText';
                const currentValue = valueFor(e);
                const inputType = inputTypeFor(meta.type);
                const editedBadge = edits[e.settingKey] != null;
                const sourceBadge = e.source === 'stored' ? 'stored' : 'default';

                const formatHint = [
                  meta.format,
                  meta.example && !meta.format ? `e.g. ${meta.example}` : null,
                  e.defaultValue && e.defaultValue !== currentValue ? `default: ${e.defaultValue}` : null,
                ].filter(Boolean).join(' · ');

                return (
                  <div key={e.settingKey}>
                    {/* Label row: friendly title + api-key chip + status */}
                    <div className="row space" style={{ alignItems: 'baseline', marginBottom: 2 }}>
                      <div style={{ fontWeight: 600 }}>{label}</div>
                      <div className="small muted" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <code style={{ fontSize: 11, opacity: 0.7 }}>{e.settingKey}</code>
                        <span className={`badge ${editedBadge ? 'warn' : ''}`} style={{ fontSize: 10 }}>
                          {editedBadge ? 'edited' : sourceBadge}
                        </span>
                      </div>
                    </div>

                    {/* Description */}
                    {meta.description && (
                      <p className="small muted" style={{ marginTop: 2, marginBottom: 6 }}>
                        {meta.description}
                      </p>
                    )}

                    {/* Input */}
                    {isLong ? (
                      <textarea
                        className="input"
                        rows={meta.type === 'longText' ? 8 : 6}
                        value={currentValue}
                        placeholder={meta.example || ''}
                        onChange={(ev) => setEdits({ ...edits, [e.settingKey]: ev.target.value })}
                        style={meta.format === 'HTML' ? { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 } : undefined}
                      />
                    ) : meta.type === 'color' ? (
                      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                        <input
                          type="color"
                          value={/^#[0-9a-fA-F]{6}$/.test(currentValue) ? currentValue : '#000000'}
                          onChange={(ev) => setEdits({ ...edits, [e.settingKey]: ev.target.value.toUpperCase() })}
                          style={{ width: 48, height: 40, padding: 2, border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer' }}
                          aria-label={`${label} picker`}
                        />
                        <input
                          className="input"
                          type="text"
                          value={currentValue}
                          placeholder={meta.example || '#000000'}
                          onChange={(ev) => setEdits({ ...edits, [e.settingKey]: ev.target.value })}
                          style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', maxWidth: 160 }}
                        />
                      </div>
                    ) : (
                      <input
                        className="input"
                        type={inputType}
                        value={currentValue}
                        placeholder={meta.example || ''}
                        min={meta.min}
                        max={meta.max}
                        onChange={(ev) => setEdits({ ...edits, [e.settingKey]: ev.target.value })}
                      />
                    )}

                    {/* Format hint */}
                    {formatHint && (
                      <div className="small muted" style={{ marginTop: 4, fontSize: 12 }}>
                        {formatHint}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
